import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { newSectionId } from "@/lib/care-plan-form-utils"

function SortableSection({ section, selectedFieldId, onSelectField, onUpdateSection, onRemoveSection, onRemoveField, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: section.id })
  const style = { transform: CSS.Transform.toString(transform), transition }
  return (
    <div ref={setNodeRef} style={style} className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <button type="button" className="cursor-grab p-1" {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        <Input
          value={section.label}
          onChange={(e) => onUpdateSection(section.id, { label: e.target.value })}
          className="font-medium flex-1"
        />
        <Button type="button" size="icon" variant="ghost" onClick={() => onRemoveSection(section.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {children}
    </div>
  )
}

function SortableField({ field, isSelected, onSelect, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: field.id })
  const style = { transform: CSS.Transform.toString(transform), transition }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm cursor-pointer ${isSelected ? "border-accent bg-accent/10" : "border-border"}`}
      onClick={() => onSelect(field.id)}
    >
      <button type="button" className="cursor-grab p-0.5" {...attributes} {...listeners} onClick={(e) => e.stopPropagation()}>
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <span className="flex-1 truncate">{field.label || field.id}</span>
      <span className="text-[10px] text-muted-foreground">{field.type}</span>
      <Button type="button" size="icon" variant="ghost" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); onRemove(field.id) }}>
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  )
}

export function TemplateCanvas({
  definition,
  onChange,
  selectedFieldId,
  onSelectField,
  onAddFieldToSection,
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const sections = definition?.sections || []

  const setSections = (next) => onChange({ ...definition, sections: next })

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const activeSec = sections.find((s) => s.fields?.some((f) => f.id === active.id))
    const overSec = sections.find((s) => s.fields?.some((f) => f.id === over.id))
    if (activeSec && overSec && activeSec.id === overSec.id) {
      const fields = activeSec.fields || []
      const oldIdx = fields.findIndex((f) => f.id === active.id)
      const newIdx = fields.findIndex((f) => f.id === over.id)
      const nextFields = arrayMove(fields, oldIdx, newIdx)
      setSections(sections.map((s) => (s.id === activeSec.id ? { ...s, fields: nextFields } : s)))
    } else if (sections.some((s) => s.id === active.id) && sections.some((s) => s.id === over.id)) {
      const oldIdx = sections.findIndex((s) => s.id === active.id)
      const newIdx = sections.findIndex((s) => s.id === over.id)
      setSections(arrayMove(sections, oldIdx, newIdx))
    }
  }

  const updateSection = (sid, patch) => {
    setSections(sections.map((s) => (s.id === sid ? { ...s, ...patch } : s)))
  }

  const removeSection = (sid) => setSections(sections.filter((s) => s.id !== sid))

  const removeField = (sid, fid) => {
    setSections(
      sections.map((s) =>
        s.id === sid ? { ...s, fields: (s.fields || []).filter((f) => f.id !== fid) } : s
      )
    )
    if (selectedFieldId === fid) onSelectField(null)
  }

  const allFieldIds = sections.flatMap((s) => (s.fields || []).map((f) => f.id))
  const sectionIds = sections.map((s) => s.id)

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={[...sectionIds, ...allFieldIds]} strategy={verticalListSortingStrategy}>
        <div className="space-y-4">
          {sections.map((sec) => (
            <SortableSection
              key={sec.id}
              section={sec}
              selectedFieldId={selectedFieldId}
              onSelectField={onSelectField}
              onUpdateSection={updateSection}
              onRemoveSection={removeSection}
              onRemoveField={(fid) => removeField(sec.id, fid)}
            >
              <SortableContext items={(sec.fields || []).map((f) => f.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1 pl-6">
                  {(sec.fields || []).map((field) => (
                    <SortableField
                      key={field.id}
                      field={field}
                      isSelected={selectedFieldId === field.id}
                      onSelect={onSelectField}
                      onRemove={(fid) => removeField(sec.id, fid)}
                    />
                  ))}
                </div>
              </SortableContext>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-full ml-6"
                onClick={() => onAddFieldToSection(sec.id)}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Câmp în secțiune
              </Button>
            </SortableSection>
          ))}
          <Button
            type="button"
            variant="outline"
            className="rounded-full w-full"
            onClick={() =>
              setSections([...sections, { id: newSectionId(), label: `Secțiune ${sections.length + 1}`, fields: [] }])
            }
          >
            <Plus className="h-4 w-4 mr-1" /> Secțiune nouă
          </Button>
        </div>
      </SortableContext>
    </DndContext>
  )
}
